/**
 * Supabase Client Configuration
 * 
 * This file initializes the Supabase client for authentication and database access.
 * TypeScript-ready structure for future migration.
 */

import { createClient } from '@supabase/supabase-js';

// Get Supabase configuration from environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate configuration
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('❌ CRITICAL: Missing Supabase configuration!');
  console.error('Required environment variables:');
  console.error('  - VITE_SUPABASE_URL:', supabaseUrl ? '✅ Set' : '❌ Missing');
  console.error('  - VITE_SUPABASE_ANON_KEY:', supabaseAnonKey ? '✅ Set' : '❌ Missing');
  console.error('Please check your .env file at the root of your project.');
} else {
  console.log('✅ Supabase client configured successfully');
}

/**
 * Supabase client instance
 * Used for authentication, database queries, and storage
 */
export const supabase = createClient(supabaseUrl || '', supabaseAnonKey || '', {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: window.localStorage,
  },
});

// Add global error handler for Supabase operations
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_OUT') {
    console.log('🔓 User signed out');
  }
});

/**
 * Helper function to get the current user session
 * @returns {Promise<Session | null>}
 */
export const getSession = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) {
      console.error('❌ Error getting session:', error);
      throw error;
    }
    return session;
  } catch (error) {
    console.error('❌ Failed to get session:', error);
    return null;
  }
};

/**
 * Helper function to get the current user
 * @returns {Promise<User | null>}
 */
export const getCurrentUser = async () => {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) {
      console.error('❌ Error getting user:', error);
      throw error;
    }
    return user;
  } catch (error) {
    console.error('❌ Failed to get user:', error);
    return null;
  }
};

/**
 * Auth state change listener
 * @param {Function} callback - Callback function to handle auth state changes
 * @returns {Object} Subscription object with unsubscribe method
 */
export const onAuthStateChange = (callback) => {
  return supabase.auth.onAuthStateChange(callback);
};
